import { motion } from 'framer-motion';
import { products } from '../../data/products';
import { LiquidCard } from '../ui/Card';
import { Badge } from '../ui/Badge';
import Lottie from 'lottie-react';
import fireAnimation from '../../assets/fire.json';

export const Hero = () => {
  return (
    <>
      {/* ── HERO VISUAL ─────────────────────────────────────────── */}
      <section id="home" className="relative h-screen min-h-[600px] overflow-hidden">
        {/* Background — GIF no mobile, vídeo no desktop */}
        <div className="absolute inset-0">
          <img
            src="/images/fundo-mobile.gif"
            alt=""
            aria-hidden="true"
            className="md:hidden w-full h-full object-cover object-center"
          />
          <video
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
            className="hidden md:block w-full h-full object-cover"
          >
            <source src="/images/bg-optimized.mp4" type="video/mp4" />
          </video>

          {/* Overlay gradiente */}
          <div
            className="absolute inset-0"
            style={{
              background: `
                radial-gradient(ellipse at 20% 50%, rgba(139, 0, 0, 0.35) 0%, transparent 50%),
                radial-gradient(ellipse at 80% 20%, rgba(212, 175, 55, 0.15) 0%, transparent 50%),
                radial-gradient(ellipse at 50% 80%, rgba(128, 0, 32, 0.25) 0%, transparent 50%),
                linear-gradient(180deg, rgba(10,10,10,0.65) 0%, rgba(26,10,14,0.55) 50%, rgba(10,10,10,0.75) 100%)
              `,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80" />
        </div>

        {/* Título central */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full px-6 pt-20 pb-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="flex flex-col items-center gap-4 sm:gap-5 w-full max-w-2xl mx-auto"
          >
            {/* Badge "Em breve" */}
            <motion.span
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-accent-gold/40 bg-accent-gold/10 text-accent-gold text-[10px] sm:text-xs uppercase tracking-widest font-semibold"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-accent-gold animate-pulse" />
              Em breve
            </motion.span>

            {/* Título principal */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight flex flex-wrap items-center justify-center gap-x-2"
            >
              <span className="text-white">Eros em</span>
              <span className="text-fire font-bold">
                CHAMAS
              </span>
              <Lottie
                animationData={fireAnimation}
                loop
                autoplay
                style={{ width: 76, height: 76, flexShrink: 0 }}
              />
            </motion.h1>

            {/* Subtítulo */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-white/60 text-sm sm:text-base md:text-lg max-w-xs sm:max-w-sm md:max-w-md leading-relaxed"
            >
              Uma experiência exclusiva chega em breve à Erohome.
            </motion.p>

            {/* CTA */}
            <motion.a
              href="https://t.me/erohome_bot"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.55 }}
              className="liquid-button mt-1 px-8 py-3 text-sm font-semibold tracking-wide"
            >
              Ser notificado
            </motion.a>
          </motion.div>
        </div>

        {/* Gradiente inferior */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-black to-transparent" />
      </section>

      {/* ── PRODUTOS ────────────────────────────────────────────── */}
      <section id="products" className="relative bg-black py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 * index }}
              >
                <LiquidCard className="group h-full flex flex-col">
                  {/* Imagem do Produto */}
                  <div className="relative h-64 overflow-hidden rounded-t-lg">
                    <img
                      src={product.image}
                      alt={product.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute top-4 right-4">
                      <Badge variant="premium">{product.category}</Badge>
                    </div>
                  </div>

                  {/* Conteúdo do Card */}
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {product.title}
                    </h3>
                    <p className="text-white/70 mb-4 line-clamp-2 flex-1">
                      {product.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {product.features.map((feature, idx) => (
                        <Badge key={idx} variant="default">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-auto">
                      <div>
                        <span className="text-sm text-white/60">A partir de</span>
                        <p className="text-2xl font-bold text-accent-gold">
                          {product.price}
                        </p>
                      </div>
                      <a
                        href={product.botLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="liquid-button text-sm"
                      >
                        Conhecer Agora
                      </a>
                    </div>
                  </div>
                </LiquidCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
